import ConfirmDialog from "./ConfirmDialog";
import { useDeleteMeetingMutation } from "./queries.generated";
import { useDispatch } from "react-redux";
import { showNotification } from "../actions";
import { fieldErrorToMessage } from "conference/tools";
import { defineMessage } from "react-intl";

type Props = {
    meetingId: number;
    isOpen: boolean;
    close: () => void;
    onSuccess?: () => void;
    name: string;
};

const DeleteMeetingDialog = ({ meetingId, isOpen, close, onSuccess, name }: Props) => {
    const dispatch = useDispatch();

    // We pass variables when executing to ensure latest meetingId if prop changes
    const [deleteMeetingMutation] = useDeleteMeetingMutation();

    async function handleConfirm() {
        try {
            const { data, errors } = await deleteMeetingMutation({ variables: { meetingId } });

            // Transport / resolver level errors (GraphQL errors array)
            if (errors && errors.length) {
                dispatch(
                    showNotification({
                        message: defineMessage({ id: "error.unexpected", defaultMessage: "Unexpected error!" }),
                        variant: "error",
                    }),
                );
                return;
            }

            // Business validation errors are returned in the payload (array of FieldError) when deletion failed
            const fieldErrors = data?.deleteMeeting;
            if (fieldErrors && fieldErrors.length) {
                dispatch(showNotification({ message: fieldErrorToMessage(fieldErrors[0]), variant: "error" }));
                return;
            }

            dispatch(
                showNotification({
                    message: defineMessage({
                        id: "meeting.success.delete",
                        defaultMessage: "The meeting has been deleted.",
                    }),
                    variant: "success",
                }),
            );
            onSuccess?.();
        } catch {
            // Network / unexpected runtime error
            dispatch(
                showNotification({
                    message: defineMessage({ id: "error.network", defaultMessage: "Network error occurred" }),
                    variant: "error",
                }),
            );
        }
    }

    return (
        <ConfirmDialog
            message={defineMessage({
                id: "deleteConfirmGeneric",
                defaultMessage: 'Do you really want to delete "{name}"?',
            })}
            messageVars={{ name }}
            onConfirm={handleConfirm}
            isOpen={isOpen}
            close={close}
            onCancel={() => null}
            // Optionally you could pass a loading state to disable buttons if ConfirmDialog supports it
            // loading={loading}
        />
    );
};

export default DeleteMeetingDialog;
